import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  adCatalogApi,
  adReviewApi,
  type AdCatalogItem,
  type AdCatalogDetailResponse,
  type CatalogSummary,
  type ReviewStatus,
  type PersonaAttribution,
} from '@/services/api';
import {
  Image,
  Video,
  FileText,
  DollarSign,
  Eye,
  MousePointer,
  ShoppingCart,
  ArrowLeft,
  Loader2,
  Play,
  CheckCircle2,
  AlertCircle,
  BarChart3,
  Filter,
  Star,
  TrendingUp,
  Users,
  Target,
  Sparkles,
  XCircle,
  Heart,
  MessageCircle,
  Share2,
  Bookmark,
  ThumbsUp,
  ChevronDown,
  Maximize2,
  Instagram,
  Monitor,
  Globe,
  ExternalLink,
  GitBranch,
  ScanSearch,
} from 'lucide-react';
import { clsx } from 'clsx';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCurrency(val: number): string {
  if (val >= 1000) return `$${(val / 1000).toFixed(1)}k`;
  return `$${val.toFixed(2)}`;
}

function formatNumber(val: number): string {
  if (val >= 1_000_000) return `${(val / 1_000_000).toFixed(1)}M`;
  if (val >= 1_000) return `${(val / 1_000).toFixed(1)}k`;
  return String(val);
}

const VERDICT_STYLES: Record<string, string> = {
  strong: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  promising: 'bg-blue-50 text-blue-700 border-blue-200',
  needs_work: 'bg-amber-50 text-amber-700 border-amber-200',
  misaligned: 'bg-rose-50 text-rose-700 border-rose-200',
};

function fbVideoEmbedUrl(videoId: string): string {
  return `https://www.facebook.com/video/embed?video_id=${videoId}`;
}

// ---------------------------------------------------------------------------
// Creative Thumbnail — handles image / video / missing gracefully
// ---------------------------------------------------------------------------

function CreativeThumbnail({
  ad,
  className = '',
  showPlayIcon = true,
  preferFullRes = false,
  fitNatural = false,
}: {
  ad: AdCatalogItem;
  className?: string;
  showPlayIcon?: boolean;
  preferFullRes?: boolean;
  fitNatural?: boolean;
}) {
  const [imgError, setImgError] = useState(false);
  const src = preferFullRes
    ? (ad.image_url || ad.thumbnail_url)
    : (ad.thumbnail_url || ad.image_url);
  const isVideo = ad.media_type === 'video' || Boolean(ad.video_id);

  if (src && !imgError) {
    if (fitNatural) {
      return (
        <div className={clsx('relative overflow-hidden flex justify-center bg-slate-50', className)}>
          <img
            src={src}
            alt={ad.ad_name}
            className="w-full h-auto rounded-lg"
            style={{ maxHeight: 600 }}
            onError={() => setImgError(true)}
          />
          {isVideo && showPlayIcon && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-10 h-10 rounded-full bg-black/50 flex items-center justify-center">
                <Play className="w-5 h-5 text-white fill-white" />
              </div>
            </div>
          )}
        </div>
      );
    }
    return (
      <div className={clsx('relative overflow-hidden', className)}>
        <img
          src={src}
          alt={ad.ad_name}
          className="w-full h-full object-cover"
          onError={() => setImgError(true)}
        />
        {isVideo && showPlayIcon && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-10 h-10 rounded-full bg-black/50 flex items-center justify-center">
              <Play className="w-5 h-5 text-white fill-white" />
            </div>
          </div>
        )}
      </div>
    );
  }

  const MediaIcon = isVideo ? Video : ad.media_type === 'image' ? Image : FileText;
  return (
    <div className={clsx('flex flex-col items-center justify-center bg-slate-100 gap-2 p-3', className)}>
      <MediaIcon className="w-6 h-6 text-slate-300 shrink-0" />
      {ad.body && (
        <p className="text-[10px] text-slate-400 text-center line-clamp-3 leading-snug">{ad.body}</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Facebook Video Embed
// ---------------------------------------------------------------------------

function VideoEmbed({ videoId, gcsUrl, thumbnailUrl }: { videoId: string; gcsUrl?: string | null; thumbnailUrl?: string | null }) {
  const [showEmbed, setShowEmbed] = useState(false);
  const [aspectRatio, setAspectRatio] = useState<number | null>(null);

  if (gcsUrl) {
    return (
      <div className="relative w-full flex justify-center rounded-lg overflow-hidden bg-slate-50">
        <video
          className="w-full rounded-lg"
          style={aspectRatio ? { aspectRatio: `${aspectRatio}` } : { maxHeight: 600 }}
          src={gcsUrl}
          controls
          playsInline
          preload="metadata"
          onLoadedMetadata={(e) => {
            const v = e.currentTarget;
            if (v.videoWidth && v.videoHeight) {
              setAspectRatio(v.videoWidth / v.videoHeight);
            }
          }}
        />
      </div>
    );
  }

  if (!showEmbed) {
    return (
      <div
        className="relative w-full cursor-pointer group rounded-lg overflow-hidden bg-slate-800"
        onClick={() => setShowEmbed(true)}
      >
        {thumbnailUrl ? (
          <img
            src={thumbnailUrl}
            alt="Video thumbnail"
            className="w-full rounded-lg"
            style={{ maxHeight: 600 }}
          />
        ) : (
          <div className="w-full bg-slate-800 rounded-lg" style={{ aspectRatio: '16/9' }} />
        )}
        <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-colors rounded-lg">
          <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
            <Play className="w-6 h-6 text-slate-800 fill-slate-800 ml-0.5" />
          </div>
        </div>
        <div className="absolute bottom-2 right-2 text-[10px] text-white/70 bg-black/50 px-1.5 py-0.5 rounded">
          Facebook Video
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
      <iframe
        className="absolute inset-0 w-full h-full rounded-lg"
        src={fbVideoEmbedUrl(videoId)}
        allowFullScreen
        allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
        title="Facebook video"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Social Preview Mockup
// ---------------------------------------------------------------------------

type PreviewPlatform = 'facebook' | 'instagram' | 'instagram-story';

function FacebookFeedPreview({ ad }: { ad: AdCatalogItem }) {
  const isVideo = ad.media_type === 'video' || Boolean(ad.video_id);

  return (
    <div className="max-w-sm mx-auto bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden font-sans">
      {/* Header */}
      <div className="p-3 flex items-center gap-2">
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-teal-400 flex items-center justify-center text-white text-xs font-bold shrink-0">
          FW
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-semibold text-slate-900">Fishewear</span>
            <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">Sponsored</span>
          </div>
          <div className="flex items-center gap-1 text-[11px] text-slate-500">
            <Globe className="w-2.5 h-2.5" />
            <span>Sponsored · Fishing Apparel</span>
          </div>
        </div>
        <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
      </div>

      {/* Copy */}
      {ad.body && (
        <div className="px-3 pb-2 text-sm text-slate-800 leading-snug line-clamp-4">
          {ad.body}
        </div>
      )}

      {/* Creative */}
      <div className="w-full bg-slate-100" style={{ minHeight: 200 }}>
        {isVideo && ad.video_id ? (
          <VideoEmbed videoId={ad.video_id} gcsUrl={ad.gcs_video_url} thumbnailUrl={ad.image_url || ad.thumbnail_url} />
        ) : (
          <CreativeThumbnail
            ad={ad}
            className="w-full h-56"
            showPlayIcon={false}
            preferFullRes
          />
        )}
      </div>

      {/* Destination */}
      {ad.destination_url && (
        <div className="px-3 py-2 border-t border-slate-100 bg-slate-50 flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-[10px] text-slate-400 uppercase tracking-wide truncate">
              {new URL(ad.destination_url).hostname.replace('www.', '')}
            </div>
            <div className="text-xs font-semibold text-slate-700 truncate">
              {ad.title || ad.ad_name}
            </div>
          </div>
          {ad.cta_type && (
            <button className="ml-3 shrink-0 text-xs bg-slate-200 hover:bg-slate-300 text-slate-700 font-semibold px-3 py-1.5 rounded-md transition-colors">
              {ad.cta_type.replace(/_/g, ' ')}
            </button>
          )}
        </div>
      )}

      {/* Engagement row */}
      <div className="px-3 py-1.5 border-t border-slate-100">
        <div className="flex items-center gap-1 text-xs text-slate-500 mb-1.5">
          <span className="inline-flex items-center -space-x-1">
            <span className="w-4 h-4 rounded-full bg-blue-500 flex items-center justify-center">
              <ThumbsUp className="w-2.5 h-2.5 text-white fill-white" />
            </span>
            <span className="w-4 h-4 rounded-full bg-rose-500 flex items-center justify-center">
              <Heart className="w-2.5 h-2.5 text-white fill-white" />
            </span>
          </span>
          <span className="ml-1">{formatNumber(Math.floor(ad.clicks * 0.3))} · {formatNumber(Math.floor(ad.clicks * 0.08))} comments</span>
        </div>
        <div className="flex border-t border-slate-100 pt-1.5 -mx-1">
          {(['Like', 'Comment', 'Share'] as const).map((action) => (
            <button
              key={action}
              className="flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold text-slate-500 hover:bg-slate-50 py-1 rounded transition-colors"
            >
              {action === 'Like' && <ThumbsUp className="w-3.5 h-3.5" />}
              {action === 'Comment' && <MessageCircle className="w-3.5 h-3.5" />}
              {action === 'Share' && <Share2 className="w-3.5 h-3.5" />}
              {action}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function InstagramFeedPreview({ ad }: { ad: AdCatalogItem }) {
  const isVideo = ad.media_type === 'video' || Boolean(ad.video_id);

  return (
    <div className="max-w-xs mx-auto bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden font-sans">
      {/* Header */}
      <div className="p-3 flex items-center gap-2">
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 p-0.5">
          <div className="w-full h-full rounded-full bg-white flex items-center justify-center">
            <div className="w-5 h-5 rounded-full bg-gradient-to-br from-blue-500 to-teal-400 flex items-center justify-center text-white text-[8px] font-bold">
              FW
            </div>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <span className="text-sm font-semibold text-slate-900">fishewear</span>
          <span className="ml-1.5 text-[10px] text-slate-400">Sponsored</span>
        </div>
        <ChevronDown className="w-4 h-4 text-slate-400" />
      </div>

      {/* Square creative */}
      <div className="w-full aspect-square bg-slate-100 relative">
        {isVideo && ad.video_id ? (
          <VideoEmbed videoId={ad.video_id} gcsUrl={ad.gcs_video_url} thumbnailUrl={ad.image_url || ad.thumbnail_url} />
        ) : (
          <CreativeThumbnail
            ad={ad}
            className="w-full h-full"
            showPlayIcon={isVideo}
            preferFullRes
          />
        )}
      </div>

      {/* Actions */}
      <div className="px-3 pt-2">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-4">
            <Heart className="w-6 h-6 text-slate-700" />
            <MessageCircle className="w-6 h-6 text-slate-700" />
            <Share2 className="w-6 h-6 text-slate-700" />
          </div>
          <Bookmark className="w-6 h-6 text-slate-700" />
        </div>
        <div className="text-xs font-semibold text-slate-800 mb-1">
          {formatNumber(Math.floor(ad.clicks * 0.4))} likes
        </div>
        {ad.body && (
          <div className="text-xs text-slate-700 leading-snug line-clamp-3">
            <span className="font-semibold">fishewear</span> {ad.body}
          </div>
        )}

        {/* CTA */}
        {ad.cta_type && (
          <button className="mt-2 w-full text-xs bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 rounded-lg transition-colors">
            {ad.cta_type.replace(/_/g, ' ')}
          </button>
        )}
        <div className="text-[10px] text-slate-400 mt-1.5 mb-2">
          View all comments
        </div>
      </div>
    </div>
  );
}

function InstagramStoryPreview({ ad }: { ad: AdCatalogItem }) {
  const isVideo = ad.media_type === 'video' || Boolean(ad.video_id);

  return (
    <div className="mx-auto bg-black rounded-3xl shadow-xl overflow-hidden font-sans" style={{ width: 260, height: 460 }}>
      {/* Status bar */}
      <div className="flex gap-1 px-3 pt-2">
        {[1, 2, 3].map((i) => (
          <div key={i} className={clsx('flex-1 h-0.5 rounded-full', i === 2 ? 'bg-white' : 'bg-white/30')} />
        ))}
      </div>

      {/* Header */}
      <div className="flex items-center gap-2 px-3 pt-2 pb-1">
        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-teal-400 flex items-center justify-center text-white text-[9px] font-bold ring-2 ring-white">
          FW
        </div>
        <span className="text-xs text-white font-semibold">fishewear</span>
        <span className="text-[10px] text-white/60 ml-1">Sponsored</span>
      </div>

      {/* Full-bleed creative */}
      <div className="flex-1 relative" style={{ height: 340 }}>
        {isVideo && ad.video_id ? (
          <div className="absolute inset-0">
            <VideoEmbed videoId={ad.video_id} gcsUrl={ad.gcs_video_url} thumbnailUrl={ad.image_url || ad.thumbnail_url} />
          </div>
        ) : (
          <CreativeThumbnail ad={ad} className="absolute inset-0 w-full h-full" showPlayIcon={isVideo} preferFullRes />
        )}

        {/* Overlay text */}
        {ad.body && (
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
            <p className="text-white text-xs line-clamp-3 leading-snug">{ad.body}</p>
          </div>
        )}
      </div>

      {/* Swipe up */}
      <div className="flex flex-col items-center py-2 gap-1">
        <div className="w-4 h-0.5 bg-white/60 rounded-full" />
        <span className="text-[10px] text-white/70 uppercase tracking-widest">
          {ad.cta_type ? ad.cta_type.replace(/_/g, ' ') : 'Swipe Up'}
        </span>
      </div>
    </div>
  );
}

function SocialPreviewMockup({ ad, onClose }: { ad: AdCatalogItem; onClose: () => void }) {
  const [platform, setPlatform] = useState<PreviewPlatform>('facebook');

  const tabs: { id: PreviewPlatform; label: string; icon: React.ElementType }[] = [
    { id: 'facebook', label: 'Facebook Feed', icon: Monitor },
    { id: 'instagram', label: 'Instagram Feed', icon: Instagram },
    { id: 'instagram-story', label: 'Story', icon: Maximize2 },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-slate-900 rounded-2xl shadow-2xl overflow-hidden flex flex-col"
        style={{ width: 520, maxHeight: '90vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-700">
          <div>
            <h3 className="text-white font-semibold text-sm">Social Preview</h3>
            <p className="text-slate-400 text-xs mt-0.5 truncate max-w-xs">{ad.ad_name}</p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white p-1.5 rounded-lg hover:bg-slate-700 transition-colors"
          >
            <XCircle className="w-5 h-5" />
          </button>
        </div>

        {/* Platform tabs */}
        <div className="flex gap-1 px-4 pt-3">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setPlatform(t.id)}
              className={clsx(
                'flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg transition-colors',
                platform === t.id
                  ? 'bg-brand-teal text-navy-900'
                  : 'text-slate-400 hover:text-white hover:bg-slate-700',
              )}
            >
              <t.icon className="w-3.5 h-3.5" />
              {t.label}
            </button>
          ))}
        </div>

        {/* Preview */}
        <div className="flex-1 overflow-y-auto p-6 flex items-start justify-center">
          {platform === 'facebook' && <FacebookFeedPreview ad={ad} />}
          {platform === 'instagram' && <InstagramFeedPreview ad={ad} />}
          {platform === 'instagram-story' && <InstagramStoryPreview ad={ad} />}
        </div>

        {/* Footer note */}
        <div className="px-5 pb-4 text-[11px] text-slate-500 text-center">
          Simulated preview — actual display may vary. Engagement metrics are estimated.
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Summary Cards
// ---------------------------------------------------------------------------

function SummaryCards({ summary }: { summary: CatalogSummary }) {
  const cards = [
    { label: 'Total Ads', value: formatNumber(summary.total_ads), icon: FileText },
    { label: 'Total Spend', value: formatCurrency(summary.total_spend), icon: DollarSign },
    { label: 'Impressions', value: formatNumber(summary.total_impressions), icon: Eye },
    { label: 'Avg CTR', value: `${summary.avg_ctr.toFixed(2)}%`, icon: MousePointer },
    { label: 'Purchases', value: formatNumber(summary.total_purchases), icon: ShoppingCart },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
      {cards.map((c) => (
        <div key={c.label} className="bg-white rounded-xl border border-slate-200 p-4 flex flex-col gap-1">
          <div className="flex items-center gap-2 text-slate-500 text-xs font-medium">
            <c.icon className="w-3.5 h-3.5" />
            {c.label}
          </div>
          <div className="text-lg font-semibold text-slate-800">{c.value}</div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Ad Card (catalog grid)
// ---------------------------------------------------------------------------

function AdCard({ ad, onClick }: { ad: AdCatalogItem; onClick: () => void }) {
  const isVideo = ad.media_type === 'video' || Boolean(ad.video_id);

  return (
    <button
      onClick={onClick}
      className="bg-white rounded-xl border border-slate-200 hover:border-brand-teal/40 hover:shadow-md transition-all text-left flex flex-col overflow-hidden group"
    >
      <div className="h-36 relative">
        <CreativeThumbnail ad={ad} className="absolute inset-0 w-full h-full" showPlayIcon={isVideo} />
        <div className="absolute top-2 right-2">
          <span className="text-[10px] font-medium bg-white/90 text-slate-600 px-1.5 py-0.5 rounded shadow-sm">
            {ad.media_type || (ad.video_id ? 'VIDEO' : 'UNKNOWN')}
          </span>
        </div>
      </div>
      <div className="p-3 flex-1 flex flex-col gap-1.5">
        <div className="text-sm font-medium text-slate-800 line-clamp-2 leading-tight">
          {ad.ad_name || ad.title || 'Untitled Ad'}
        </div>
        <div className="text-xs text-slate-500 line-clamp-1">{ad.campaign_name}</div>
        <div className="mt-auto grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
          <div><span className="text-slate-400">Spend</span><span className="ml-1 font-medium text-slate-700">{formatCurrency(ad.spend)}</span></div>
          <div><span className="text-slate-400">CTR</span><span className="ml-1 font-medium text-slate-700">{ad.ctr.toFixed(2)}%</span></div>
          <div><span className="text-slate-400">Clicks</span><span className="ml-1 font-medium text-slate-700">{formatNumber(ad.clicks)}</span></div>
          <div><span className="text-slate-400">Purchases</span><span className="ml-1 font-medium text-slate-700">{formatNumber(ad.purchases)}</span></div>
        </div>
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Filter Bar
// ---------------------------------------------------------------------------

function FilterBar({ campaign, setCampaign, mediaType, setMediaType, campaigns, mediaTypes }: {
  campaign: string; setCampaign: (v: string) => void;
  mediaType: string; setMediaType: (v: string) => void;
  campaigns: string[]; mediaTypes: string[];
}) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="flex items-center gap-1.5 text-slate-500">
        <Filter className="w-4 h-4" />
        <span className="text-xs font-medium">Filters</span>
      </div>
      <select value={campaign} onChange={(e) => setCampaign(e.target.value)}
        className="text-xs border border-slate-200 rounded-lg px-3 py-1.5 bg-white text-slate-700 focus:ring-1 focus:ring-brand-teal/40">
        <option value="">All Campaigns</option>
        {campaigns.map((c) => <option key={c} value={c}>{c.length > 40 ? c.slice(0, 40) + '…' : c}</option>)}
      </select>
      <select value={mediaType} onChange={(e) => setMediaType(e.target.value)}
        className="text-xs border border-slate-200 rounded-lg px-3 py-1.5 bg-white text-slate-700 focus:ring-1 focus:ring-brand-teal/40">
        <option value="">All Media</option>
        {mediaTypes.map((m) => <option key={m} value={m}>{m}</option>)}
      </select>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Review Results Panel
// ---------------------------------------------------------------------------

function ReviewPanel({ review }: { review: ReviewStatus }) {
  const synthesis = review.review_synthesis as Record<string, unknown> | null;
  const verdict = (synthesis?.verdict as string) || '';
  const verdictLabel = (synthesis?.verdict_label as string) || verdict;
  const summary = (synthesis?.executive_summary as string) || '';
  const strengths = (synthesis?.strengths as string[]) || [];
  const weaknesses = (synthesis?.weaknesses as string[]) || [];
  const recommendations = (synthesis?.recommendations as { action: string; rationale: string; priority: string }[]) || [];

  return (
    <div className="space-y-4">
      {verdict && (
        <div className={clsx('inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm font-semibold',
          VERDICT_STYLES[verdict] || 'bg-slate-50 text-slate-600 border-slate-200')}>
          <Star className="w-4 h-4" />
          {verdictLabel}
        </div>
      )}
      {summary && <p className="text-sm text-slate-700 leading-relaxed">{summary}</p>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {strengths.length > 0 && (
          <div className="bg-emerald-50/50 rounded-lg p-3 border border-emerald-100">
            <div className="text-xs font-semibold text-emerald-700 mb-2 flex items-center gap-1">
              <CheckCircle2 className="w-3.5 h-3.5" /> Strengths
            </div>
            <ul className="space-y-1">{strengths.map((s, i) => <li key={i} className="text-xs text-emerald-800">{s}</li>)}</ul>
          </div>
        )}
        {weaknesses.length > 0 && (
          <div className="bg-amber-50/50 rounded-lg p-3 border border-amber-100">
            <div className="text-xs font-semibold text-amber-700 mb-2 flex items-center gap-1">
              <AlertCircle className="w-3.5 h-3.5" /> Weaknesses
            </div>
            <ul className="space-y-1">{weaknesses.map((w, i) => <li key={i} className="text-xs text-amber-800">{w}</li>)}</ul>
          </div>
        )}
      </div>

      {review.persona_scores && review.persona_scores.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1">
            <Users className="w-3.5 h-3.5" /> Persona Fit Ranking
          </div>
          <div className="space-y-2">
            {review.persona_scores.slice(0, 5).map((p, i) => (
              <div key={p.persona_id} className="flex items-center gap-3 bg-white rounded-lg border border-slate-100 p-2.5">
                <div className="w-6 h-6 rounded-full bg-brand-teal/10 text-brand-teal-dark flex items-center justify-center text-xs font-bold">{i + 1}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-800 truncate">{p.persona_name}</div>
                  <div className="text-xs text-slate-500 truncate">{p.rationale}</div>
                </div>
                <div className="flex items-center gap-3 text-xs text-slate-600 shrink-0">
                  <div title="Relevance"><Target className="w-3 h-3 inline mr-0.5" />{p.relevance}</div>
                  <div title="Resonance"><Sparkles className="w-3 h-3 inline mr-0.5" />{p.resonance}</div>
                  <div title="Conversion"><TrendingUp className="w-3 h-3 inline mr-0.5" />{p.conversion_likelihood}</div>
                  <div className="font-bold text-brand-teal-dark">{p.overall}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {review.scenario_matches && review.scenario_matches.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1">
            <BarChart3 className="w-3.5 h-3.5" /> Buyer Scenarios
          </div>
          <div className="grid gap-2">
            {review.scenario_matches.map((s: Record<string, unknown>, i: number) => (
              <div key={i} className="bg-blue-50/50 rounded-lg border border-blue-100 p-3">
                <div className="text-sm font-medium text-blue-900">{s.title as string}</div>
                <p className="text-xs text-blue-700 mt-1">{s.description as string}</p>
                {(s.triggers as string[] | undefined)?.length ? (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {(s.triggers as string[]).map((t, j) => (
                      <span key={j} className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">{t}</span>
                    ))}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      )}

      {recommendations.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-slate-700 mb-2">Recommendations</div>
          <div className="space-y-2">
            {recommendations.map((r, i) => (
              <div key={i} className="flex items-start gap-2 text-xs">
                <span className={clsx('shrink-0 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase',
                  r.priority === 'high' ? 'bg-rose-100 text-rose-700' : r.priority === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600')}>
                  {r.priority}
                </span>
                <div>
                  <span className="font-medium text-slate-800">{r.action}</span>
                  <span className="text-slate-500 ml-1">— {r.rationale}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Ad Detail View
// ---------------------------------------------------------------------------

function PersonaAttributionSection({ attribution }: { attribution: PersonaAttribution[] }) {
  if (!attribution.length) return null;
  const totalVisitors = attribution.reduce((s, p) => s + p.visitors, 0);
  const maxVisitors = Math.max(...attribution.map((p) => p.visitors));

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-1.5">
        <GitBranch className="w-4 h-4 text-fuchsia-600" /> Persona Attribution
      </h3>
      <div className="text-xs text-slate-400 mb-3">
        {totalVisitors.toLocaleString()} visitors resolved across {attribution.length} persona{attribution.length !== 1 ? 's' : ''}
      </div>
      <div className="space-y-3">
        {attribution.map((p) => {
          const pct = totalVisitors > 0 ? (p.visitors / totalVisitors * 100) : 0;
          return (
            <div key={p.persona_name} className="space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-slate-700">{p.persona_name}</span>
                <span className="text-[10px] text-slate-400">{pct.toFixed(1)}% · {p.visitors.toLocaleString()} visitors</span>
              </div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-fuchsia-500 rounded-full transition-all"
                  style={{ width: `${(p.visitors / maxVisitors) * 100}%` }}
                />
              </div>
              <div className="flex gap-4 text-[10px] text-slate-400">
                <span>Views: {p.pageviewers.toLocaleString()}</span>
                <span>ATC: {p.cart_adders.toLocaleString()}</span>
                <span>Purchases: {p.purchasers.toLocaleString()}</span>
                {p.avg_persona_ltv > 0 && <span>Avg LTV: ${p.avg_persona_ltv.toFixed(2)}</span>}
                {p.avg_match_score > 0 && <span>Match: {p.avg_match_score}%</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AdDetailView({ adId, clientUid, onBack }: { adId: string; clientUid: string; onBack: () => void }) {
  const [detail, setDetail] = useState<AdCatalogDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [review, setReview] = useState<ReviewStatus | null>(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [error, setError] = useState('');
  const [personaAttr, setPersonaAttr] = useState<PersonaAttribution[]>([]);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    setLoading(true);
    adCatalogApi.getAd(adId, clientUid)
      .then(setDetail)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
    adCatalogApi.getAdPersonaAttribution(adId)
      .then(setPersonaAttr)
      .catch(() => {});
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [adId, clientUid]);

  const startReview = useCallback(async () => {
    setReviewLoading(true);
    try {
      const res = await adReviewApi.startReview(adId, clientUid);
      setReview(res);
      pollRef.current = setInterval(async () => {
        try {
          const updated = await adReviewApi.getReview(res.review_id);
          setReview(updated);
          if (updated.status === 'completed' || updated.status === 'failed') {
            clearInterval(pollRef.current!);
            setReviewLoading(false);
          }
        } catch { clearInterval(pollRef.current!); setReviewLoading(false); }
      }, 2000);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Review failed');
      setReviewLoading(false);
    }
  }, [adId, clientUid]);

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="w-6 h-6 animate-spin text-brand-teal" />
    </div>
  );

  if (error || !detail) return (
    <div className="text-center py-12">
      <XCircle className="w-8 h-8 text-rose-400 mx-auto mb-2" />
      <p className="text-sm text-rose-600">{error || 'Ad not found'}</p>
      <button onClick={onBack} className="mt-4 text-sm text-brand-teal hover:underline">Back to catalog</button>
    </div>
  );

  const ad = detail.ad;
  const isVideo = ad.media_type === 'video' || Boolean(ad.video_id);

  return (
    <>
      {showPreview && <SocialPreviewMockup ad={ad} onClose={() => setShowPreview(false)} />}

      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3 flex-wrap">
          <button onClick={onBack} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors">
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </button>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold text-slate-800 truncate">{ad.ad_name || ad.title || 'Untitled Ad'}</h2>
            <p className="text-xs text-slate-500">{ad.campaign_name}</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowPreview(true)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 text-sm font-medium transition-colors">
              <Monitor className="w-4 h-4" />
              Preview
            </button>
            {ad.destination_url && (
              <a href={ad.destination_url} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 text-sm transition-colors">
                <ExternalLink className="w-4 h-4" />
              </a>
            )}
            <button onClick={startReview} disabled={reviewLoading}
              className={clsx('flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                reviewLoading ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : 'bg-brand-teal text-white hover:bg-brand-teal-dark')}>
              {reviewLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
              {reviewLoading ? 'Reviewing…' : 'Run Review'}
            </button>
            {ad.creative_id && (
              <a
                href={`/ad-detail/?client=${encodeURIComponent(clientUid || '')}&creative=${encodeURIComponent(
                  ad.creative_id,
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 rounded-lg border border-brand-teal/30 bg-brand-teal/5 text-brand-teal hover:bg-brand-teal/10 text-sm font-medium transition-colors"
                title="Open the ad-detail SPA with full taxonomy + lens + creative review intelligence"
              >
                <ScanSearch className="w-4 h-4" />
                Deep Dive
              </a>
            )}
          </div>
        </div>

        {/* Main grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Creative */}
          <div className="lg:col-span-1 space-y-4">
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              {/* Video embed or image — natural aspect ratio */}
              {isVideo && ad.video_id ? (
                <div className="p-3">
                  <VideoEmbed videoId={ad.video_id} gcsUrl={ad.gcs_video_url} thumbnailUrl={ad.image_url || ad.thumbnail_url} />
                </div>
              ) : (
                <div className="bg-slate-50">
                  <CreativeThumbnail ad={ad} className="w-full" showPlayIcon={false} preferFullRes fitNatural />
                </div>
              )}
              <div className="p-4 space-y-3">
                {ad.title && (
                  <div>
                    <div className="text-xs font-medium text-slate-500">Headline</div>
                    <div className="text-sm text-slate-800 font-medium">{ad.title}</div>
                  </div>
                )}
                {ad.body && (
                  <div>
                    <div className="text-xs font-medium text-slate-500">Body</div>
                    <div className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{ad.body}</div>
                  </div>
                )}
                <div className="flex flex-wrap gap-1.5">
                  {ad.cta_type && (
                    <span className="text-xs bg-brand-teal/10 text-brand-teal-dark px-2 py-0.5 rounded-full font-medium">
                      {ad.cta_type.replace(/_/g, ' ')}
                    </span>
                  )}
                  {ad.media_type && (
                    <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{ad.media_type}</span>
                  )}
                  {ad.status && (
                    <span className={clsx('text-xs px-2 py-0.5 rounded-full',
                      ad.status === 'ACTIVE' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500')}>
                      {ad.status}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Right: Performance + Review */}
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-1.5">
                <BarChart3 className="w-4 h-4" /> Performance
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {[
                  { label: 'Spend', value: formatCurrency(ad.spend) },
                  { label: 'Impressions', value: formatNumber(ad.impressions) },
                  { label: 'Clicks', value: formatNumber(ad.clicks) },
                  { label: 'CTR', value: `${ad.ctr.toFixed(2)}%` },
                  { label: 'Purchases', value: formatNumber(ad.purchases) },
                  { label: 'CPA', value: ad.cpa ? formatCurrency(ad.cpa) : '—' },
                ].map((m) => (
                  <div key={m.label}>
                    <div className="text-xs text-slate-500">{m.label}</div>
                    <div className="text-base font-semibold text-slate-800">{m.value}</div>
                  </div>
                ))}
              </div>
            </div>

            {detail.daily.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <h3 className="text-sm font-semibold text-slate-700 mb-3">Daily Performance</h3>
                <div className="max-h-48 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="text-slate-500 border-b border-slate-100">
                      <tr>
                        <th className="text-left py-1.5 pr-3">Date</th>
                        <th className="text-right py-1.5 px-2">Spend</th>
                        <th className="text-right py-1.5 px-2">Impr.</th>
                        <th className="text-right py-1.5 px-2">Clicks</th>
                        <th className="text-right py-1.5 pl-2">CTR</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.daily.map((d) => (
                        <tr key={d.date} className="border-b border-slate-50">
                          <td className="py-1.5 pr-3 text-slate-700">{d.date}</td>
                          <td className="py-1.5 px-2 text-right text-slate-700">{formatCurrency(d.spend)}</td>
                          <td className="py-1.5 px-2 text-right text-slate-600">{formatNumber(d.impressions)}</td>
                          <td className="py-1.5 px-2 text-right text-slate-600">{formatNumber(d.clicks)}</td>
                          <td className="py-1.5 pl-2 text-right text-slate-600">{d.ctr.toFixed(2)}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {personaAttr.length > 0 && <PersonaAttributionSection attribution={personaAttr} />}

            {(review || reviewLoading) && (
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-1.5">
                  <Sparkles className="w-4 h-4" /> Creative Review
                </h3>
                {review?.status === 'completed' ? (
                  <ReviewPanel review={review} />
                ) : review?.status === 'failed' ? (
                  <div className="text-sm text-rose-600">Review failed: {review.errors?.join(', ')}</div>
                ) : (
                  <div className="flex items-center gap-3 text-sm text-slate-500">
                    <Loader2 className="w-4 h-4 animate-spin text-brand-teal" />
                    <span>{review?.current_phase ? `Running: ${review.current_phase.replace(/_/g, ' ')}…` : 'Starting review…'}</span>
                    {review?.progress !== undefined && review.progress > 0 && (
                      <div className="flex-1 max-w-xs bg-slate-100 rounded-full h-1.5">
                        <div className="h-full rounded-full bg-brand-teal transition-all" style={{ width: `${review.progress}%` }} />
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Main Catalog Page
// ---------------------------------------------------------------------------

export function AdCatalogPage() {
  const { adId } = useParams();
  const navigate = useNavigate();

  const [clientUid] = useState('fishewear');
  const [ads, setAds] = useState<AdCatalogItem[]>([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState<CatalogSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [campaign, setCampaign] = useState('');
  const [mediaType, setMediaType] = useState('');
  const [page, setPage] = useState(0);
  const [error, setError] = useState('');

  const PAGE_SIZE = 24;
  const campaigns = summary?.top_campaigns.map((c) => c.campaign_name) || [];
  const mediaTypes = summary?.media_type_breakdown.map((m) => m.media_type) || [];

  const fetchAds = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [adsRes, summaryRes] = await Promise.all([
        adCatalogApi.listAds({ client_uid: clientUid, campaign: campaign || undefined, media_type: mediaType || undefined, limit: PAGE_SIZE, offset: page * PAGE_SIZE }),
        summary ? Promise.resolve(summary) : adCatalogApi.getSummary(clientUid),
      ]);
      setAds(adsRes.ads);
      setTotal(adsRes.total);
      if (!summary) setSummary(summaryRes);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load ads');
    } finally {
      setLoading(false);
    }
  }, [clientUid, campaign, mediaType, page, summary]);

  useEffect(() => { if (!adId) fetchAds(); }, [fetchAds, adId]);

  if (adId) {
    return <AdDetailView adId={adId} clientUid={clientUid} onBack={() => navigate('/ad-catalog')} />;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold text-slate-800">Ad Catalog</h1>
        <p className="text-sm text-slate-500 mt-1">
          Browse extracted Facebook ads for <span className="font-medium">{clientUid}</span>
        </p>
      </div>

      {summary && <SummaryCards summary={summary} />}

      <FilterBar campaign={campaign} setCampaign={(v) => { setCampaign(v); setPage(0); }}
        mediaType={mediaType} setMediaType={(v) => { setMediaType(v); setPage(0); }}
        campaigns={campaigns} mediaTypes={mediaTypes} />

      {error && <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-sm text-rose-700">{error}</div>}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-brand-teal" />
        </div>
      ) : (
        <>
          <div className="text-xs text-slate-500">Showing {ads.length} of {total} ads</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
            {ads.map((ad) => (
              <AdCard key={ad.ad_id} ad={ad} onClick={() => navigate(`/ad-catalog/${ad.ad_id}`)} />
            ))}
          </div>
          {total > PAGE_SIZE && (
            <div className="flex items-center justify-center gap-3 pt-2">
              <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}
                className="text-xs px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-40">
                Previous
              </button>
              <span className="text-xs text-slate-500">Page {page + 1} of {Math.ceil(total / PAGE_SIZE)}</span>
              <button onClick={() => setPage((p) => p + 1)} disabled={(page + 1) * PAGE_SIZE >= total}
                className="text-xs px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-40">
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
